import { useState, useEffect } from "react";
import { userService } from "../../services/userService";
import { logger } from "../../utils/logger";
import { useTenant } from "../../contexts/TenantContext";

interface UseContactAddressAutoFillProps {
  contactId?: string;
  setAddress: (address: string) => void;
}

interface UseContactAddressAutoFillResult {
  isLoading: boolean;
  error: string | null;
  addressFound: boolean;
}

/**
 * Hook to auto-fill address field with contact address information when a contact_id is provided
 */
export const useContactAddressAutoFill = ({
  contactId,
  setAddress,
}: UseContactAddressAutoFillProps): UseContactAddressAutoFillResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addressFound, setAddressFound] = useState(false);
  const [addressLoaded, setAddressLoaded] = useState(false);
  const { config } = useTenant();

  useEffect(() => {
    const fetchContactAddress = async () => {
      // Only fetch once and only if we have a contact ID and config
      if (addressLoaded || !contactId || !config) return;

      try {
        setIsLoading(true);
        setError(null);

        // Use config.id as the organization ID
        const organizationId = config.id;

        logger.debug("Fetching contact address for auto-fill", {
          contactId,
          organizationId,
        });
        const contact = await userService.findContactById(
          contactId,
          organizationId
        );

        if (contact) {
          logger.debug("Contact found, checking for address information", {
            contactId,
          });

          // Check if contact has address information
          // The address might be stored in different ways depending on the data structure
          const address = (contact as any).address || {};

          // Format the address
          const addressParts = [];
          if (address.street) addressParts.push(address.street);
          if (address.city) addressParts.push(address.city);
          if (address.state) addressParts.push(address.state);
          if (address.zip) addressParts.push(address.zip);

          const formattedAddress = addressParts.join(", ");

          // Auto-fill address field
          if (formattedAddress) {
            setAddress(formattedAddress);
            setAddressFound(true);
          }
        } else {
          logger.debug("Contact address not found for auto-fill", {
            contactId,
          });
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch contact address";
        logger.error("Error fetching contact address", {
          contactId,
          error: errorMessage,
        });
        setError(errorMessage);
      } finally {
        setIsLoading(false);
        setAddressLoaded(true);
      }
    };

    fetchContactAddress();
  }, [contactId, setAddress, addressLoaded, config]);

  return { isLoading, error, addressFound };
};

export default useContactAddressAutoFill;
